import React, { useState, useEffect } from "react";
import { Dialog, Checkbox, FormControlLabel } from "@mui/material";
import { styled } from "@mui/material/styles";
import SuccessModal from "./SuccessModal";
import ErrorModal from "./ErrorModal";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { transferToPrimaryWallet } from "../../../../services/transfer_comission_to_primary_service";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

// Function to detect if dark mode is active from Tailwind's dark class
const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

const StyledDialog = styled(Dialog)(({ theme }) => {
  const isDark = isDarkModeActive();
  return {
    "& .MuiBackdrop-root": {
      backdropFilter: "blur(6px)",
      backgroundColor: "rgba(0, 0, 0, 0.2)",
    },
    "& .MuiPaper-root": {
      width: "90%",
      maxWidth: "500px",
      border: `1px solid ${isDark ? "#444" : "#d32f2f"}`,
      borderRadius: 12,
      padding: theme.spacing(2),
      backgroundColor: isDark ? "#1f2937" : theme.palette.background.paper,
    },
  };
});

export default function TransferModal({ open, onClose, onTransferSuccess }) {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state.auth.user);
  const [amount, setAmount] = useState("");
  const [isTransferSuccess, setIsTransferSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [failed, setFailed] = useState(false);
  const [transferAll, setTransferAll] = useState(false);
  const [isDark, setIsDark] = useState(false);

  // Listen for dark mode changes
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  const closeSuccessModal = () => {
    setIsTransferSuccess(false);
    handleClose(); 
  };

  const closeErrorModal = () => setFailed(false);

  const { mutate, isPending: loading } = useMutation({
    mutationFn: (payload) => transferToPrimaryWallet(userInfo?.uid, payload),
    onSuccess: () => {
      setIsTransferSuccess(true);
      onTransferSuccess?.();
    },
    onError: (error) => {
      console.log("Transfer error:", error);
      const message =
        error?.response?.data?.error || t("transferModal.errors.transferFailed");
      setErrorMessage(message);
      setFailed(true);
    },
  });

  const handleClose = () => {
    setAmount("");
    setErrorMessage("");
    setTransferAll(false);
    onClose();
  };

  const handleTransfer = () => {
    setErrorMessage("");

    if (!transferAll && (!amount || isNaN(amount))) {
      setErrorMessage(
        !amount 
          ? t("transferModal.errors.amountRequired")
          : t("transferModal.errors.amountNumber")
      );
      return;
    }

    const payload = {
      available_amount: transferAll,
      amount: transferAll ? 0 : Number(amount),
    };

    mutate(payload);
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <SuccessModal
        open={isTransferSuccess}
        onClose={closeSuccessModal}
        amount={transferAll ? t("transferModal.allAvailable") : amount}
      />

      <ErrorModal
        open={failed}
        onClose={closeErrorModal}
        title={t("transferModal.errors.transferFailedTitle")}
        error={errorMessage}
        onRetry={handleTransfer}
      >
        <div className="w-full">
          <div className={`flex items-center justify-between ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            <h3>{t("transferModal.amount")}</h3>
            <h3>{transferAll ? t("transferModal.allAvailable") : amount}</h3>
          </div>
          <div className={`flex items-center justify-between mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            <h3>{t("transferModal.date")}</h3>
            <h3>{new Date().toLocaleDateString()}</h3>
          </div>
        </div>
      </ErrorModal>

      <div className="px-5 py-3">
        <h3 className={`my-3 text-[16px] font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {t("transferModal.title")}
        </h3>

        <div className="flex flex-col">
          <label className={`my-2 font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            {t("transferModal.enterAmount")}
          </label>

          <input
            disabled={transferAll}
            onChange={(e) => {
              setErrorMessage("");
              setAmount(e.target.value);
            }}
            value={amount}
            type="text"
            placeholder={t("transferModal.amountPlaceholder")}
            className={`w-full border rounded-md py-2 px-3 transition-all duration-300 outline-none ${
              transferAll
                ? `bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed ${isDark ? 'border-gray-700' : 'border-gray-300'}`
                : `border-[#D0D5DD] dark:border-gray-600 focus:border-red-500 dark:focus:border-red-500 ${isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`
            }`}
          />

          {errorMessage && (
            <p className="text-sm text-red-500 dark:text-red-400 mt-1">{errorMessage}</p>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={transferAll}
                onChange={(e) => {
                  setTransferAll(e.target.checked);
                  if (e.target.checked) {
                    setAmount("");
                    setErrorMessage("");
                  }
                }}
                sx={{
                  color: "#CD0C02",
                  "&.Mui-checked": {
                    color: "#CD0C02",
                  },
                  "& .MuiSvgIcon-root": {
                    color: isDark ? "#CD0C02" : "#CD0C02",
                  },
                }}
              />
            }
            label={t("transferModal.transferAll")}
            className={`mt-4 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
          />
        </div>

        <div className="w-full flex justify-end mt-6 gap-3">
          <button
            onClick={handleClose}
            className={`py-2 px-6 border rounded-md w-[30%] text-sm transition-colors ${
              isDark 
                ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                : 'border-gray-400 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t("transferModal.cancel")}
          </button>
          <button
            onClick={handleTransfer}
            className={`text-white w-[70%] py-2 px-6 rounded-md text-sm transition-colors ${
              loading
                ? "bg-[#9b150d] dark:bg-red-800 cursor-not-allowed"
                : "bg-[#CD0C02] hover:bg-[#b01001] dark:bg-red-700 dark:hover:bg-red-800"
            }`}
            disabled={loading}
          >
            {loading ? t("transferModal.transferring") : t("transferModal.transfer")}
          </button>
        </div>
      </div>
    </StyledDialog>
  );
}